import React, { useMemo, useState } from 'react'
import { Button, Select, Typography, theme } from 'antd'
import { DownOutlined, UpOutlined } from '@ant-design/icons'
import { Layer } from '../../../services/models'
import { appliedFilterCount, DictionaryUi, GroupFilters, LayerGroup, rangeLabel } from './types'
import GroupCard from './GroupCard'

const { Text } = Typography

type Props = {
  layers: Layer[],
  groups: LayerGroup[],
  dictUi: DictionaryUi,
  onAdd: (layerId: number) => void,
  onApply: (groupKey: string, filters: GroupFilters) => void,
  onRemove: (groupKey: string) => void
}

const ColorDot: React.FC<{ color: string }> = ({ color }) => (
  <span style={{ width: 12, height: 12, borderRadius: '50%', background: color, flex: '0 0 auto' }} />
)

const LayerGroupsPanel: React.FC<Props> = ({ layers, groups, dictUi, onAdd, onApply, onRemove }) => {
  const [collapsed, setCollapsed] = useState(true)
  const [pendingLayer, setPendingLayer] = useState<number | undefined>(undefined)
  const { token } = theme.useToken()

  // Слой нельзя добавить повторно: каждая группа соответствует ровно одному
  // слою, иначе один и тот же объект рисовался бы дважды разными цветами.
  const available = useMemo(() => {
    const used = new Set(groups.map(g => g.layerId))
    return layers.filter(l => !used.has(l.id))
  }, [layers, groups])

  const total = groups.reduce((acc, g) => acc + g.items.length, 0)

  // После применения панель сворачивается в сводку выбранного.
  const handleApply = (groupKey: string, filters: GroupFilters) => {
    onApply(groupKey, filters)
    setCollapsed(true)
  }

  const summary = groups.length === 0
    ? 'ничего не выбрано'
    : `${groups.length} шт · ${total} об.`

  return (
    <div style={{
      position: 'absolute',
      left: 16,
      top: 16,
      width: 320,
      // top без bottom: панель растёт вниз от заголовка, но не выше экрана.
      // Иначе раскрытая превратилась бы в высокий пустой белый прямоугольник.
      maxHeight: 'calc(100% - 32px)',
      // Drawer в antd имеет z-index 1000, поэтому панель под ним: открытый
      // Drawer перекрывает панель. Контролы Leaflet живут в .leaflet-top и
      // .leaflet-bottom с z-index 1000, но стоят в правом верхнем и нижнем
      // углах, а панель — слева, так что геометрически они не пересекаются.
      zIndex: 900,
      display: 'flex',
      flexDirection: 'column',
      // Без overflow карточка со скруглением «протыкает» содержимое.
      overflow: 'hidden',
      background: token.colorBgElevated,
      borderRadius: token.borderRadiusLG,
      boxShadow: token.boxShadowSecondary,
      border: `1px solid ${token.colorBorderSecondary}`
    }}>
      <div style={{ padding: '10px 12px', flex: '0 0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: collapsed ? 0 : 10 }}>
        <Text strong style={{ flex: '0 0 auto', marginRight: 8 }}>Слои на карте</Text>
        <Text type="secondary" style={{ fontSize: 12, flex: 1 }}>{summary}</Text>
        <Button
          size="small"
          type="text"
          icon={collapsed ? <DownOutlined/> : <UpOutlined/>}
          title={collapsed ? 'Развернуть' : 'Свернуть'}
          onClick={() => setCollapsed(old => !old)}
        />
        </div>
      </div>

      <div style={{ padding: '0 12px 12px', flex: '1 1 auto', overflowY: 'auto' }}>
      {collapsed && groups.length > 0 && groups.map(g => {
        const layer = layers.find(l => l.id === g.layerId)
        const filters = appliedFilterCount(g.filters)
        // Числовые диапазоны в сводке: подписи собираются только по атрибутам,
        // у которых диапазон действительно задан, иначе было бы пустое место.
        const ranges = layer?.attributeNumbers ?? []
        const numeric = ranges
          .map(an => rangeLabel(g.filters, an.id))
          .filter(label => label !== '')
          .join(', ')
        return (
          <div
            key={g.key}
            onClick={() => setCollapsed(false)}
            title="Развернуть и изменить фильтры"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 8px',
              marginBottom: 4,
              borderRadius: 6,
              cursor: 'pointer',
              background: '#fafafa'
            }}
          >
            <ColorDot color={g.color} />
            <Text ellipsis style={{ flex: 1 }}>{layer?.name ?? `Слой #${g.layerId}`}</Text>
            <Text type="secondary" style={{ fontSize: 12, flex: '0 0 auto' }}>
              {filters > 0 ? `${filters} ф. · ` : ''}{numeric !== '' ? `${numeric} · ` : ''}{g.items.length} об.
            </Text>
          </div>
        )
      })}

      {!collapsed && (
        <>
          {groups.map(g => {
            const layer = layers.find(l => l.id === g.layerId)
            // Слой мог исчезнуть из списка — карточку показывать не с чем.
            if (!layer) return null
            return (
              <GroupCard
                key={g.key}
                group={g}
                layer={layer}
                dictUi={dictUi}
                onApply={handleApply}
                onRemove={onRemove}
              />
            )
          })}

          {available.length === 0
            ? <Text type="secondary">Все доступные слои уже добавлены на карту.</Text>
            : (
              <Select
                value={pendingLayer}
                placeholder="Добавить слой..."
                style={{ width: '100%' }}
                showSearch
                optionFilterProp="label"
                onChange={(layerId: number) => {
                  // Сбрасываем значение, чтобы селект снова показал placeholder.
                  setPendingLayer(undefined)
                  onAdd(layerId)
                }}
                options={available.map(l => ({ value: l.id, label: l.name }))}
              />
              )}
        </>
      )}
      </div>
    </div>
  )
}

export default LayerGroupsPanel
